import React, { useState, useRef } from "react";
import { LuEye } from "react-icons/lu";
import PropTypes from "prop-types";
import "./_assetCell.scss";

const AssetCell = ({
  dragProps = {},
  ratio = 1,
  selected = false,
  flush,
  onPreview,
  previewIcon,
  onAdd,
  actions,
  previewLabel,
  label,
  title,
  badge,
  dragPreview,
  children,
}) => {
  const { className: dragClassName = "", ...restDrag } = dragProps;
  const [busy, setBusy] = useState(false);
  // Pointer-down position, so a drag is not treated as a tap-to-add (a drag
  // released off-canvas would otherwise still fire a click and add the box).
  const downRef = useRef(null);
  const classes = `cmp-cell${selected ? " is-selected" : ""}${
    flush ? " cmp-cell--flush" : ""
  } ${dragClassName}`.trim();
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn(e);
  };

  const handlePointerDown = (e) => {
    downRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleClick = (e) => {
    if (busy || !onAdd) return;
    const down = downRef.current;
    downRef.current = null;
    // > 6px between press and release ⇒ it was a drag, not a tap ⇒ ignore.
    if (down && Math.abs(e.clientX - down.x) + Math.abs(e.clientY - down.y) > 6) return;
    const result = onAdd(e);
    if (result && typeof result.then === "function") {
      setBusy(true);
      Promise.resolve(result).finally(() => setBusy(false));
    }
  };

  return (
    <div
      {...restDrag}
      className={classes}
      style={{ "--cmp-cell-ratio": ratio }}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <div className="cmp-cell__frame" title={title}>
        {children}
        {busy ? (
          <div className="cmp-cell__loading">
            <span className="cmp-cell__spinner" />
          </div>
        ) : null}
      </div>

      {dragPreview ? (
        <div
          className="cmp-cell__dragsrc"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: -99999,
            top: 0,
            width: 320,
            pointerEvents: "none",
            visibility: "hidden",
          }}
        >
          {dragPreview}
        </div>
      ) : null}

      {label ? (
        <div className="cmp-cell__label" title={title}>
          {label}
        </div>
      ) : null}

      {(onPreview || (actions && actions.length > 0)) && (
        <div className="cmp-cell__actions">
          {onPreview ? (
            <button
              type="button"
              className="cmp-cell__actionBtn"
              title={previewLabel}
              onClick={stop(onPreview)}
            >
              {previewIcon || <LuEye size={14} strokeWidth={2.25} />}
            </button>
          ) : null}
          {(actions || []).filter(Boolean).map((a) => (
            <button
              key={a.key}
              type="button"
              className={`cmp-cell__actionBtn${a.danger ? " is-danger" : ""}`}
              title={a.title}
              aria-label={a.title}
              onClick={stop(a.onClick)}
            >
              {a.icon}
            </button>
          ))}
        </div>
      )}
      {badge}
    </div>
  );
};

AssetCell.propTypes = {
  dragProps: PropTypes.object,
  ratio: PropTypes.number,
  selected: PropTypes.bool,
  flush: PropTypes.bool,
  onPreview: PropTypes.func,
  previewIcon: PropTypes.node,
  onAdd: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.object),
  previewLabel: PropTypes.string,
  label: PropTypes.node,
  title: PropTypes.string,
  badge: PropTypes.node,
  dragPreview: PropTypes.node,
  children: PropTypes.node,
};

export default AssetCell;
